/**
 * Marketing Onboarding Page
 *
 * Main orchestrator for the guided onboarding flow.
 * Renders different views based on current onboarding status.
 */

import { useAuth } from '@trafiflow/shared-ui'
import { Header } from '@trafiflow/shared-ui'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { MarketingSidebar } from '../../components/MarketingSidebar'
// NOTE: Uncomment when enabling redirect after onboarding completion
// import { useEffect } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { ROUTES } from '@/shared/config'
import OnboardingBrandSelect from './OnboardingBrandSelect'
import OnboardingProgress from './OnboardingProgress'
import OnboardingPresentation from './OnboardingPresentation'

export default function MarketingOnboardingPage() {
  const { user, logout } = useAuth()
  const { state, isLoading } = useOnboarding()

  // NOTE: Redirect disabled for testing - uncomment when ready for production
  // const navigate = useNavigate()
  // useEffect(() => {
  //   if (state.status === 'completed') {
  //     navigate(ROUTES.MARKETING.HOME, { replace: true })
  //   }
  // }, [state.status, navigate])

  const handleLogout = async () => {
    try {
      await logout()
      window.location.href = '/login'
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-tf-bg-muted">
        <MarketingSidebar locked />
        <div className="flex-1 flex flex-col min-w-0">
          <Header
            user={
              user
                ? {
                    id: user.userId,
                    email: user.email,
                    name: user.email.split('@')[0],
                    tenant_id: user.tenantId,
                    role: user.roles?.[0],
                  }
                : null
            }
            onLogout={handleLogout}
            searchPlaceholder="Search..."
            showSearch={false}
          />
          <main className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-2 border-tf-border border-t-primary rounded-full animate-spin" />
              <p className="text-sm text-tf-text-muted">Loading...</p>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-tf-bg-muted">
      <MarketingSidebar locked />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          user={
            user
              ? {
                  id: user.userId,
                  email: user.email,
                  name: user.email.split('@')[0],
                  tenant_id: user.tenantId,
                  role: user.roles?.[0],
                }
              : null
          }
          onLogout={handleLogout}
          searchPlaceholder="Search..."
          showSearch={false}
        />
        <main className="flex-1 overflow-auto">
          {state.status === 'brand_select' && <OnboardingBrandSelect />}
          {state.status === 'pipeline_running' && <OnboardingProgress />}
          {(state.status === 'presenting' || state.status === 'completed') && <OnboardingPresentation />}
        </main>
      </div>
    </div>
  )
}
